// Mes Achats - Historique des achats
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag, Package, Star, MessageCircle, Eye, Truck, CheckCircle,
  Clock, MapPin, ChevronRight, RefreshCw, AlertTriangle, ArrowLeft, X, User, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { TicketModal } from '../../components/TicketModal';
import CockpitParticulierBottomNav from '../../components/layout/CockpitParticulierBottomNav';

function AchatCard({ achat, onClick }) {
  const statusConfig = {
    delivered: { label: 'Livré', bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle },
    shipping: { label: 'En livraison', bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: Truck },
    pending: { label: 'En attente', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: Clock },
    problem: { label: 'Problème', bgColor: 'bg-red-100', textColor: 'text-red-700', icon: AlertTriangle },
  };

  const status = statusConfig[achat.status] || statusConfig.delivered;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer active:scale-[0.99]"
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          {achat.image ? (
            <img src={achat.image} alt={achat.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={20} className="text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{achat.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Acheté à {achat.seller}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 flex-shrink-0 ${status.bgColor} ${status.textColor}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Prix</p>
              <p className="text-sm font-bold text-[#1E3A5F]">{achat.price} €</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date</p>
              <p className="text-xs font-medium text-gray-700">{achat.date}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Lieu</p>
              <p className="text-xs font-medium text-gray-700">{achat.ville}</p>
            </div>
            {achat.rated && (
              <div className="ml-auto">
                <span className="flex items-center gap-1 text-xs text-yellow-600">
                  <Star size={12} className="fill-yellow-500" />
                  {achat.myRating}/5
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function AchatsPage({ onSelectTool }) {
  const { user } = useCurrentUser();
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  
  // Review form
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);

  // Données démo
  const DEMO_ACHATS = [
    {
      id: '1',
      title: 'Niveau laser Bosch GLL 3-80',
      price: 280,
      seller: 'Patrick B.',
      sellerType: 'particulier', // particulier ou pro
      available: false, // Plus disponible (vendu une seule fois)
      ville: 'Lyon',
      date: '12 mai 2026',
      status: 'delivered',
      rated: true,
      myRating: 5,
      image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400',
    },
    {
      id: '2',
      title: 'Lot de pinceaux et rouleaux pro',
      price: 35,
      seller: 'Marie L.',
      sellerType: 'particulier',
      available: true, // Lot disponible
      ville: 'Paris',
      date: '8 mai 2026',
      status: 'delivered',
      rated: false,
      myRating: null,
      image: null,
    },
    {
      id: '3',
      title: 'Escabeau aluminium 3 marches',
      price: 45,
      seller: 'Jean-Pierre D.',
      sellerType: 'particulier',
      available: false, // Plus disponible
      ville: 'Marseille',
      date: '5 mai 2026',
      status: 'shipping',
      rated: false,
      myRating: null,
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
    },
  ];

  useEffect(() => {
    setTimeout(() => {
      // Toujours afficher les données démo pour la prévisualisation
      setAchats(DEMO_ACHATS);
      setLoading(false);
    }, 300);
  }, [user]);

  // Contacter le vendeur → Ouvrir le modal Ticket
  const handleContactSeller = () => {
    setShowUnavailable(false);
    setShowTicketModal(true);
  };
  
  // Racheter → Vérifier disponibilité puis ajouter au panier
  const handleRacheter = () => {
    // Vérifier si l'article est encore disponible
    if (!selectedAchat?.available) {
      setShowUnavailable(true);
      setTimeout(() => {
        setShowUnavailable(false);
      }, 2500);
    } else {
      setShowCartSuccess(true);
      setTimeout(() => {
        setShowCartSuccess(false);
        setSelectedAchat(null);
      }, 2000);
    }
  };
  
  // Ouvrir le modal d'avis
  const handleOpenReview = () => {
    setReviewRating(0);
    setReviewComment('');
    setShowReviewModal(true);
  };
  
  const handleSubmitReview = () => {
    if (reviewRating === 0 || !reviewComment.trim()) return;
    
    // Marquer comme noté
    setAchats(prev => prev.map(a => 
      a.id === selectedAchat.id ? { ...a, rated: true, myRating: reviewRating } : a
    ));
    setShowReviewModal(false);
    setSelectedAchat(null);
  };

  const totalDepense = achats.reduce((acc, a) => acc + a.price, 0);
  const achatsRecus = achats.filter(a => a.status === 'delivered').length;

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onSelectTool && (
          <button 
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Retour au tableau de bord"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Achats</h1>
          <p className="text-xs sm:text-sm text-gray-500">{achats.length} achat{achats.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <ShoppingBag size={16} className="text-purple-600 sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold text-gray-900">{achats.length}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Achats</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={16} className="text-green-600 sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold text-gray-900">{achatsRecus}</p>
              <p className="text-[10px] sm:text-xs text-gray-500">Reçus</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 sm:p-4 text-white">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <ShoppingBag size={16} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-bold">{totalDepense} €</p>
              <p className="text-[10px] sm:text-xs text-white/80">Dépensé</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : achats.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun achat</h3>
          <p className="text-gray-500 mb-4">
            Tu n'as pas encore effectué d'achat
          </p>
          {onSelectTool && (
            <Button 
              onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Découvrir la marketplace
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {achats.map((achat) => (
            <AchatCard
              key={achat.id}
              achat={achat}
              onClick={() => setSelectedAchat(achat)}
            />
          ))}
        </div>
      )}

      {/* Popup Principal - Détails de l'achat */}
      <AnimatePresence>
        {selectedAchat && !showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setSelectedAchat(null); setShowUnavailable(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              {showUnavailable ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Article plus disponible</h3>
                  <p className="text-sm text-gray-500 mb-4">Cet article a déjà été vendu et n'est plus disponible à l'achat.</p>
                  <Button
                    onClick={handleContactSeller}
                    variant="outline"
                    className="mx-auto"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Contacter le vendeur
                  </Button>
                </div>
              ) : showCartSuccess ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ajouté au panier !</h3>
                  <p className="text-sm text-gray-500">{selectedAchat?.title} a été ajouté à ton panier.</p>
                </div>
              ) : (
                <>
                  {/* Header avec image */}
                  <div className="relative">
                    <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                      {selectedAchat.image ? (
                        <img 
                          src={selectedAchat.image} 
                          alt={selectedAchat.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedAchat(null)}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <X size={18} className="text-gray-600" />
                    </button>
                    {/* Badge statut */}
                    <div className="absolute bottom-3 left-3">
                      {(() => {
                        const statusConfig = {
                          delivered: { label: 'Livré', bgColor: 'bg-green-500', icon: CheckCircle },
                          shipping: { label: 'En livraison', bgColor: 'bg-blue-500', icon: Truck },
                          pending: { label: 'En attente', bgColor: 'bg-yellow-500', icon: Clock },
                        };
                        const status = statusConfig[selectedAchat.status] || statusConfig.delivered;
                        const StatusIcon = status.icon;
                        return (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white flex items-center gap-1.5 ${status.bgColor}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedAchat.title}</h2>
                    
                    {/* Prix */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl font-bold text-[#1E3A5F]">{selectedAchat.price} €</span>
                      {selectedAchat.rated && (
                        <span className="flex items-center gap-1 text-sm text-yellow-600 ml-auto">
                          <Star size={14} className="fill-yellow-500" />
                          Mon avis : {selectedAchat.myRating}/5
                        </span>
                      )}
                    </div>

                    {/* Infos vendeur */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Acheté à</p>
                          <p className="font-medium text-gray-900">{selectedAchat.seller}</p>
                        </div>
                        <div className="ml-auto text-right">
                          <p className="text-xs text-gray-500">Lieu</p>
                          <p className="font-medium text-gray-900 text-sm flex items-center gap-1">
                            <MapPin size={12} />
                            {selectedAchat.ville}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                      <Calendar size={14} />
                      <span>Acheté le {selectedAchat.date}</span>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        onClick={handleContactSeller}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} />
                        Contacter le vendeur
                      </Button>
                      
                      {selectedAchat.status === 'delivered' && !selectedAchat.rated && (
                        <Button
                          onClick={handleOpenReview}
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white flex items-center justify-center gap-2"
                        >
                          <Star size={16} />
                          Laisser un avis
                        </Button>
                      )}
                      
                      <Button
                        onClick={handleRacheter}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Racheter cet article
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Laisser un avis */}
      <AnimatePresence>
        {showReviewModal && selectedAchat && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Laisser un avis</h3>
                <button onClick={() => setShowReviewModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-14 h-14 bg-gray-200 rounded-lg overflow-hidden">
                    {selectedAchat.image ? (
                      <img src={selectedAchat.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={20} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{selectedAchat.title}</p>
                    <p className="text-xs text-gray-500">{selectedAchat.seller}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ta note</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setReviewRating(star)}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          size={32}
                          className={`${
                            star <= (hoverRating || reviewRating)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'
                          } transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ton avis</p>
                  <textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Partage ton expérience..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button variant="outline" className="flex-1" onClick={() => setShowReviewModal(false)}>
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                  onClick={handleSubmitReview}
                  disabled={reviewRating === 0 || !reviewComment.trim()}
                >
                  Publier mon avis
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Ticket (composant réutilisable) */}
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedAchat(null); }}
        recipientName={selectedAchat?.seller}
        recipientId={selectedAchat?.id}
        type="buyer"
        onSuccess={() => setSelectedAchat(null)}
      />
      
      {/* Navbar Cockpit Particulier */}
      <CockpitParticulierBottomNav />
    </div>
  );
}
